/**
 * LivePipelineKanban — drag-droppable horizontal pipeline
 * (iter114s.76 Phase 2b).
 *
 * Each column shows count + oldest-order timer (Phase 1) AND is now
 * a @dnd-kit DroppableArea. The current smart-queue rows render as
 * Draggable order cards INSIDE their stage's column when expanded.
 * Drop fires `POST /api/admin/orders/{id}/stage` and the parent
 * triggers a refresh.
 *
 * Compact mode (default) shows only the counts + timers. Click
 * "Expand pipeline" to surface the per-stage order cards for drag.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext, useDraggable, useDroppable, closestCenter, KeyboardSensor,
  PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { ChevronRight, GripVertical, ChevronsUpDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_THEME = {
  paid:                { fill: 'bg-red-100',    text: 'text-red-700',    rail: 'bg-red-400' },
  file_review:         { fill: 'bg-orange-100', text: 'text-orange-700', rail: 'bg-orange-400' },
  proof_sent:          { fill: 'bg-amber-100',  text: 'text-amber-700',  rail: 'bg-amber-400' },
  awaiting_approval:   { fill: 'bg-yellow-100', text: 'text-yellow-700', rail: 'bg-yellow-400' },
  production:          { fill: 'bg-sky-100',    text: 'text-sky-700',    rail: 'bg-sky-400' },
  shipped:             { fill: 'bg-emerald-100', text: 'text-emerald-700', rail: 'bg-emerald-400' },
  completed:           { fill: 'bg-[#E8F1E6]', text: 'text-[#2F5D3A]',  rail: 'bg-[#6FBF73]' },
};

const formatAge = (h) => {
  if (!h || h <= 0) return '—';
  if (h < 24) return `${Math.round(h)}h old`;
  return `${Math.round(h / 24)}d old`;
};

const ageSeverity = (h, stage) => {
  const bands = { paid: 24, file_review: 24, proof_sent: 48, awaiting_approval: 72, production: 168, shipped: 336, completed: Infinity };
  const limit = bands[stage] ?? 96;
  if (!h || h <= 0) return 'ok';
  if (h > limit) return 'breach';
  if (h > limit * 0.66) return 'warn';
  return 'ok';
};

const DraggableOrderCard = ({ order }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: order.id,
    data: { stage: order.stage },
  });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 } : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-testid={`kanban-card-${order.id}`}
      className={`bg-white border border-[#6FBF73]/40 rounded-lg p-2 mb-1.5 text-[11px] shadow-sm cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-60 ring-2 ring-[#6FBF73]' : ''
      }`}
    >
      <div className="flex items-center gap-1.5">
        <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
        <span className="font-bold text-[#14281D] truncate flex-1">{order.order_number}</span>
      </div>
      <p className="text-[10px] text-[#1F3D2C]/65 truncate">{order.customer_email}</p>
      <p className="text-[10px] text-[#1F3D2C]/55 truncate">{order.first_item_product}</p>
    </div>
  );
};

const DroppableStage = ({ stage, theme, ageClass, expanded, ordersInStage, isLast }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, data: { stage: stage.id } });
  return (
    <div className="relative">
      <div
        ref={setNodeRef}
        className={`rounded-lg p-3 h-full flex flex-col ${theme.fill} ${
          isOver ? 'ring-2 ring-[#2F5D3A] ring-offset-1' : ''
        } transition`}
        data-testid={`pipeline-stage-${stage.id}`}
      >
        <span className={`text-[10px] uppercase tracking-wider font-bold ${theme.text}`}>{stage.label}</span>
        <div className="mt-1 flex items-end gap-1">
          <span className={`text-2xl font-black ${theme.text}`} data-testid={`pipeline-count-${stage.id}`}>
            {stage.count}
          </span>
          <span className={`text-[10px] mb-1 ${theme.text}/70`}>
            {stage.count === 1 ? 'order' : 'orders'}
          </span>
        </div>
        <span className={`mt-1 text-[10px] ${ageClass}`} data-testid={`pipeline-oldest-${stage.id}`}>
          {formatAge(stage.oldest_age_hours)}
        </span>
        {expanded && (
          <div className="mt-2 space-y-1 max-h-44 overflow-y-auto pr-0.5" data-testid={`pipeline-cards-${stage.id}`}>
            {ordersInStage.map((o) => <DraggableOrderCard key={o.id} order={o} />)}
            {ordersInStage.length === 0 && (
              <p className="text-[10px] italic text-[#1F3D2C]/40">Empty</p>
            )}
          </div>
        )}
        <span className={`absolute left-0 bottom-0 right-0 h-1 rounded-b-lg ${theme.rail}`} aria-hidden="true" />
      </div>
      {!isLast && !expanded && (
        <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6FBF73]/60 pointer-events-none" />
      )}
    </div>
  );
};

export const LivePipelineKanban = ({ stages, loading, queueRows = [], onStageChange }) => {
  // iter114s.77 — default expanded so drag-drop cards are immediately
  // discoverable. Operator flagged the previous collapsed-default as
  // an "easy miss" because nothing in the UI hinted that the columns
  // contained draggable order cards underneath.
  const [expanded, setExpanded] = useState(true);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const ordersByStage = useMemo(() => {
    const map = {};
    for (const row of queueRows) {
      if (!map[row.stage]) map[row.stage] = [];
      map[row.stage].push(row);
    }
    return map;
  }, [queueRows]);

  const handleDragEnd = async ({ active, over }) => {
    if (!over || !active) return;
    const targetStage = over.data?.current?.stage || over.id;
    const sourceStage = active.data?.current?.stage;
    if (!targetStage || targetStage === sourceStage) return;
    try {
      await axios.post(`${API_URL}/api/admin/orders/${active.id}/stage`, { stage: targetStage }, { withCredentials: true });
      toast.success(`Moved to ${targetStage.replace(/_/g, ' ')}.`);
      onStageChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Stage update failed.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-4 grid grid-cols-7 gap-2" aria-hidden="true">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-3 md:p-4" data-testid="pipeline-kanban">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#2F5D3A]">
          Drag cards between stages to advance an order
        </p>
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          className="text-[11px] font-semibold text-[#2F5D3A] hover:text-[#14281D] flex items-center gap-1 px-2 py-1 rounded hover:bg-[#E8F1E6] transition"
          data-testid="pipeline-expand-toggle"
        >
          <ChevronsUpDown className="w-3 h-3" />
          {expanded ? 'Collapse cards' : 'Expand cards'}
        </button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <div className="grid grid-flow-col auto-cols-[minmax(150px,1fr)] gap-2 items-stretch overflow-x-auto pb-1">
          {stages.map((stage, idx) => {
            const theme = STAGE_THEME[stage.id] || STAGE_THEME.production;
            const sev = ageSeverity(stage.oldest_age_hours, stage.id);
            const ageClass = sev === 'breach' ? 'text-red-600 font-bold'
              : sev === 'warn' ? 'text-orange-600 font-semibold'
              : 'text-[#1F3D2C]/60';
            return (
              <DroppableStage
                key={stage.id}
                stage={stage}
                theme={theme}
                ageClass={ageClass}
                expanded={expanded}
                ordersInStage={ordersByStage[stage.id] || []}
                isLast={idx === stages.length - 1}
              />
            );
          })}
        </div>
      </DndContext>
    </div>
  );
};

export default LivePipelineKanban;
